import React, { useCallback, useMemo, useRef, useState } from "react";
import { Avatar, Box, CircularProgress, Popover, TextField, Typography } from "@mui/material";
import { FiPlus, FiSearch, FiCheck, FiX } from "react-icons/fi";
import useUserSearch from "../../utils/hooks/useUserSearch";
import { initials, hueFor } from "../AssigneeGroup/utils";
import { T } from "../../pages/TodoTask/constants/todoTokens";

/**
 * AssigneeSelect — multi-member picker shaped as an avatar stack plus a ⊕.
 *
 * Why not a tags-in-input Autocomplete: chips inside the field make it grow as
 * the team grows, they fight the search caret for space, and the list has to
 * stay open while picking. Here the FIELD NEVER CHANGES HEIGHT — it is only
 * ever avatars — and all the work happens in a popover that pins the people
 * already chosen at the top.
 *
 * Props:
 *   value        – string[] of user _ids
 *   onChange     – (ids: string[], options: Option[]) => void
 *   preloaded    – Option[] { _id, label, file } so chips/avatars have labels
 *                  before the first fetch resolves (and survive a search)
 *   roles        – comma-separated role filter; omit to search every member
 *   max          – avatars shown before "+N" (default 5)
 *   size         – avatar px (default 28)
 *   disabled | placeholder | error | emptyLabel
 */
const AssigneeSelect = ({
  value = [],
  onChange,
  preloaded = [],
  roles,
  max = 5,
  size = 28,
  disabled = false,
  placeholder = "Search members…",
  error = false,
  emptyLabel = "Add assignees",
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [term, setTerm] = useState("");
  const open = Boolean(anchorEl);
  const anchorRef = useRef(null);

  // Only fetch once the popover has actually been opened — a form with three
  // of these should not fire three member queries on mount.
  const [everOpened, setEverOpened] = useState(false);
  const { users, loadingInit, loadingMore, hasMore, search, loadMore } = useUserSearch({
    roles,
    enabled: everOpened,
  });

  // People picked earlier in this session. A server-side search REPLACES the
  // visible page, so without this a selected person who is not in the current
  // results would lose their name. Written only in `toggle` — never during
  // render, which React is free to discard and re-run.
  const seen = useRef(new Map());

  const resolve = useCallback(
    (id) => {
      const key = String(id);
      return (
        users.find((u) => u._id === key) ||
        preloaded.find((o) => String(o._id) === key) ||
        seen.current.get(key) || { _id: key, label: "…", file: null }
      );
    },
    [users, preloaded],
  );

  const selected = useMemo(() => (value || []).filter(Boolean).map(resolve), [value, resolve]);
  const selectedIds = useMemo(() => new Set((value || []).map(String)), [value]);

  const emit = useCallback(
    (ids) => onChange(ids, ids.map(resolve)),
    [onChange, resolve],
  );

  const toggle = useCallback(
    (opt) => {
      seen.current.set(opt._id, opt);
      const ids = selectedIds.has(opt._id)
        ? (value || []).filter((id) => String(id) !== opt._id)
        : [...(value || []), opt._id];
      emit(ids.map(String));
    },
    [selectedIds, value, emit],
  );

  const openPopover = () => {
    if (disabled) return;
    setEverOpened(true);
    setAnchorEl(anchorRef.current);
  };
  const closePopover = () => {
    setAnchorEl(null);
    setTerm("");
    search("");
  };

  // Selected people are pinned at the top of the list, so "who is already on
  // this?" never requires scrolling and de-selecting is always one click.
  const unselected = users.filter((u) => !selectedIds.has(u._id));

  const shown = selected.slice(0, max);
  const extra = selected.length - shown.length;

  const ring = size >= 24 ? 2 : 1.5;
  const font = size >= 28 ? 11 : size >= 24 ? 9 : 8;

  const Row = ({ opt, checked }) => (
    <Box
      component="button"
      type="button"
      onClick={() => toggle(opt)}
      sx={{
        width: "100%", display: "flex", alignItems: "center", gap: "9px",
        px: "9px", py: "6px", borderRadius: "8px",
        border: "none", bgcolor: "transparent", font: "inherit", textAlign: "left",
        cursor: "pointer", transition: "background .12s",
        "&:hover": { bgcolor: checked ? T.primaryLight : T.borderLight },
        "&:focus-visible": { outline: "none", boxShadow: `0 0 0 3px ${T.primaryGlow}` },
      }}
    >
      <Avatar
        src={opt.file || undefined}
        sx={{
          width: 26, height: 26, flexShrink: 0, fontSize: "10px", fontWeight: 700,
          bgcolor: hueFor(opt._id), color: "#fff",
        }}
      >
        {initials(opt.label)}
      </Avatar>
      <Typography
        sx={{
          flex: 1, minWidth: 0, fontSize: "12.5px", fontWeight: checked ? 600 : 500,
          color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}
      >
        {opt.label}
      </Typography>
      {checked && <FiCheck size={14} color={T.primary} style={{ flexShrink: 0 }} />}
    </Box>
  );

  return (
    <>
      {/* The field: avatars + ⊕, fixed height forever. */}
      <Box
        ref={anchorRef}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={
          selected.length
            ? `${selected.length} assignee${selected.length > 1 ? "s" : ""} selected — edit`
            : emptyLabel
        }
        onClick={openPopover}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openPopover(); }
        }}
        sx={{
          display: "flex", alignItems: "center", gap: "8px", minHeight: size + 8,
          px: "6px", py: "4px", borderRadius: "8px",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.55 : 1,
          border: error ? "1px solid #EF4444" : "1px solid transparent",
          transition: "background .13s, border-color .13s",
          "&:hover": disabled ? {} : { bgcolor: T.borderLight },
          "&:focus-visible": { outline: "none", boxShadow: `0 0 0 3px ${T.primaryGlow}` },
        }}
      >
        {selected.length > 0 && (
          <Box sx={{ display: "flex", "& > *:not(:first-of-type)": { ml: `${-Math.round(size * 0.3)}px` } }}>
            {shown.map((o) => (
              <Avatar
                key={o._id}
                src={o.file || undefined}
                sx={{
                  width: size, height: size, fontSize: `${font}px`, fontWeight: 700,
                  bgcolor: hueFor(o._id), color: "#fff",
                  border: `${ring}px solid #fff`, boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                }}
              >
                {initials(o.label)}
              </Avatar>
            ))}
            {extra > 0 && (
              <Avatar
                sx={{
                  width: size, height: size, fontSize: `${font}px`, fontWeight: 800,
                  bgcolor: "#F1F5F9", color: T.textSub,
                  border: `${ring}px solid #fff`, boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                }}
              >
                +{extra}
              </Avatar>
            )}
          </Box>
        )}

        {/* The ⊕ affordance — dashed so it reads as "empty slot", not "person". */}
        <Box
          aria-hidden="true"
          sx={{
            width: size, height: size, flexShrink: 0, borderRadius: "50%",
            display: "flex", alignItems: "center", justifyContent: "center",
            border: `1.5px dashed ${T.border}`, color: T.textMuted,
            transition: "border-color .13s, color .13s, background .13s",
            ".MuiBox-root:hover > &": { borderColor: T.primary, color: T.primary, bgcolor: T.primaryLight },
          }}
        >
          <FiPlus size={Math.round(size * 0.45)} />
        </Box>

        {selected.length === 0 && (
          <Typography sx={{ fontSize: "13px", color: T.textMuted, fontStyle: "italic" }}>
            {emptyLabel}
          </Typography>
        )}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            role: "dialog",
            "aria-label": "Select assignees",
            elevation: 0,
            sx: {
              mt: 0.75, width: 300, borderRadius: "12px",
              border: `1px solid ${T.border}`, boxShadow: T.shadow.lg, overflow: "hidden",
            },
          },
        }}
        transitionDuration={{ enter: 140, exit: 90 }}
      >
        {/* Search */}
        <Box sx={{ p: "9px 10px 7px" }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={term}
            onChange={(e) => { setTerm(e.target.value); search(e.target.value); }}
            placeholder={placeholder}
            InputProps={{
              startAdornment: <FiSearch size={13} color={T.textMuted} style={{ marginRight: 7, flexShrink: 0 }} />,
              endAdornment: loadingInit ? <CircularProgress size={13} /> : null,
              sx: {
                fontSize: "12.5px", borderRadius: "8px", bgcolor: T.bg,
                "& fieldset": { borderColor: T.border },
              },
            }}
          />
        </Box>

        <Box sx={{ height: "1px", bgcolor: T.borderLight }} />

        {/* List — selected pinned on top, then everyone else */}
        <Box
          onScroll={(e) => {
            const el = e.currentTarget;
            if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) loadMore();
          }}
          sx={{
            maxHeight: 264, overflowY: "auto", p: "5px",
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": { background: T.border, borderRadius: 3 },
            "&::-webkit-scrollbar-track": { background: "transparent" },
          }}
        >
          {selected.length > 0 && (
            <>
              <Box sx={{ display: "flex", alignItems: "center", px: "9px", pt: "4px", pb: "3px" }}>
                <Typography sx={{ fontSize: "9.5px", fontWeight: 800, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  Selected · {selected.length}
                </Typography>
                <Box
                  component="button"
                  type="button"
                  onClick={() => emit([])}
                  sx={{
                    ml: "auto", display: "inline-flex", alignItems: "center", gap: "3px",
                    border: "none", bgcolor: "transparent", cursor: "pointer", p: 0,
                    fontSize: "9.5px", fontWeight: 700, color: T.textMuted, font: "inherit",
                    "&:hover": { color: "#DC2626" },
                    "&:focus-visible": { outline: "none", boxShadow: `0 0 0 3px ${T.primaryGlow}`, borderRadius: "4px" },
                  }}
                >
                  <FiX size={10} /> Clear
                </Box>
              </Box>
              {selected.map((o) => <Row key={o._id} opt={o} checked />)}
              {unselected.length > 0 && (
                <Box sx={{ height: "1px", bgcolor: T.borderLight, my: "5px", mx: "9px" }} />
              )}
            </>
          )}

          {unselected.map((o) => <Row key={o._id} opt={o} checked={false} />)}

          {loadingMore && (
            <Box sx={{ display: "flex", justifyContent: "center", py: "8px" }}>
              <CircularProgress size={15} />
            </Box>
          )}
          {!loadingInit && !loadingMore && users.length === 0 && (
            <Typography sx={{ fontSize: "12px", color: T.textMuted, textAlign: "center", py: "14px", fontStyle: "italic" }}>
              No members found
            </Typography>
          )}
          {!hasMore && !loadingMore && users.length > 0 && unselected.length === 0 && selected.length > 0 && (
            <Typography sx={{ fontSize: "11px", color: T.textMuted, textAlign: "center", py: "10px" }}>
              Everyone matching is already assigned
            </Typography>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default React.memo(AssigneeSelect);
